import { FC } from 'react';

import { ExerciseIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../../../components/hover-card/index';
import { TableRow, TableRowData } from '../../../components/table/index';
import { Tooltip } from '../../../components/tooltip/index';
import { useFormat } from '../../../hooks/index';
import { Valuation } from '../../../types/events.types';
import {
  SharePlanType,
  sharePlanTypeTitle,
  sharePlanVestingTypeTitle,
} from '../../../types/pool-plans.types';
import { VestingTask } from '../../../types/vesting-tasks.types';
import { toRound } from '../../../utils/get-rounded-number.util';
import { percentage } from '../../../utils/percentage.util';
import { toMathRound } from '../../../utils/to-math-round.util';
import { toNumber } from '../../../utils/to-number.util';
import { SummaryItem } from '../summary-item';
import { InfoCell } from './info-cell';
import {
  AllPlanColumnEnums,
  AllVestingColumnEnums,
  table,
  VestingExerciseStructureColumn,
} from './table-variant';

type TableItemProps = {
  columns: (AllPlanColumnEnums | AllVestingColumnEnums)[];
  companyId: string;
  companyIssuedShares: number;
  disableExercise?: boolean;
  onRequestExercise: (props: { id: string; type: SharePlanType }) => void;
  stakeholderId: string;
  type?: 'planInfo' | 'vestingInfo';
  valuation?: Valuation;
  vestingTask: VestingTask;
} & Optional<{
  mode: 'phoneView';
  sharePlanType: SharePlanType;
}>;

export const VestingTableItem: FC<TableItemProps> = ({
  columns = [],
  companyIssuedShares,
  disableExercise = false,
  mode,
  onRequestExercise,
  sharePlanType,
  type = 'planInfo',
  valuation,
  vestingTask: {
    id,
    nextTriggerTime,
    fullVestingCalculatedDate,
    finished,
    totalSharesCount,
    vestedSharesCount,
    exercisable,
    exercisedCount,
    grantItem: {
      stakeholder,
      id: grantId,
      plan: {
        vesting,
        basePrice,
        sarBasePrice,
        name,
        hardleValue,
        type: planType,
        rsaPurchasePrice,
        strikePrice,
        warrantPrice,
        pool: { shareClass },
      },
    },
  },
}) => {
  const { format } = useFormat();

  const vestedPercentage = percentage(toNumber(totalSharesCount), toNumber(vestedSharesCount));

  const remainingPercentage = percentage(
    toNumber(totalSharesCount),
    toNumber(totalSharesCount) - toNumber(vestedSharesCount),
  );

  const exercisableShares = toNumber(vestedSharesCount) - toNumber(exercisedCount);

  const vestingRowData: TableRowData<AllVestingColumnEnums, { planType: SharePlanType }> = {
    remaining: (
      <InfoCell
        description={`(${toRound(remainingPercentage || 0)}%)`}
        title={(toNumber(totalSharesCount) - toNumber(vestedSharesCount)).toLocaleString('en-US')}
      />
    ),
    vested: (
      <InfoCell
        description={`(${toRound(vestedPercentage || 0)}%)`}
        title={toNumber(vestedSharesCount).toLocaleString('en-US')}
      />
    ),
    'full-vesting': format(fullVestingCalculatedDate, 'dd/MM/yyyy'),
    exercised: toNumber(exercisedCount).toLocaleString('en-US'),
    exercisable: toNumber(exercisableShares).toLocaleString('en-US'),
    granted: toNumber(totalSharesCount).toLocaleString('en-US'),
    'to-be-vested': (
      toNumber(totalSharesCount) -
      (toNumber(exercisedCount) + toNumber(exercisableShares))
    ).toLocaleString('en-US'),
    'next-vesting': !finished ? format(nextTriggerTime, 'dd/MM/yyyy') : '-',
    type: vesting ? (
      mode === 'phoneView' ? (
        `${sharePlanVestingTypeTitle[vesting.type].name} (${sharePlanVestingTypeTitle[vesting.type].description})`
      ) : (
        <InfoCell
          description={sharePlanVestingTypeTitle[vesting.type].description}
          title={sharePlanVestingTypeTitle[vesting.type].name}
        />
      )
    ) : (
      '-'
    ),
    'exercise-button': !disableExercise ? (
      (id, { planType }) =>
        !stakeholder.isTerminated &&
        exercisable &&
        (exercisableShares > 0 ? (
          <HoverCard data-permission-required>
            <HoverCardTrigger className="h-fit w-fit">
              <div
                className="centered mr-5 flex size-8 cursor-pointer rounded border border-[#F2F4F7]"
                onClick={() => onRequestExercise({ id, type: planType })}
                style={{
                  boxShadow: '0px 0.89px 1.78px 0px #1018280D',
                }}
              >
                <ExerciseIcon />
              </div>
              <HoverCardContent
                align="end"
                className="centered flex h-[34px] rounded-lg bg-[#101828] px-3 py-[2px] text-xs font-semibold text-white"
              >
                <AppFormattedMessage id="EXERCISE" />
              </HoverCardContent>
            </HoverCardTrigger>
          </HoverCard>
        ) : (
          <Tooltip content={<AppFormattedMessage id="EXERCISE_NO_VESTED_SHARES_TOOLTIP" />}>
            <div
              aria-disabled="true"
              className="centered mr-5 flex size-8 cursor-not-allowed rounded border border-[#F2F4F7] opacity-40"
              style={{
                boxShadow: '0px 0.89px 1.78px 0px #1018280D',
              }}
            >
              <ExerciseIcon />
            </div>
          </Tooltip>
        ))
    ) : (
      <></>
    ),
    id,
    planType,
  };

  const planRowData: TableRowData<AllPlanColumnEnums, { planType: SharePlanType }> = {
    'base-price': basePrice || sarBasePrice,
    'current-price': toMathRound(valuation?.sharePrice)?.toLocaleString('en-US'),
    'current-valuation': (
      toNumber(valuation?.sharePrice) * toNumber(companyIssuedShares)
    ).toLocaleString('en-US'),
    'grant-id': grantId.split('-')[0].toUpperCase(),
    hurdle: hardleValue?.toLocaleString('en-US'),
    id,
    plan: (
      <Tooltip childrenWrapperClassName="max-w-[300px]" content={name}>
        {name}
      </Tooltip>
    ),
    'plan-type': <AppFormattedMessage id={sharePlanTypeTitle[planType]} />,
    'rsa-price': toNumber(rsaPurchasePrice).toLocaleString('en-US'),
    'share-class': shareClass.name,
    'strike-price': toNumber(strikePrice).toLocaleString('en-US'),
    'warrant-price': toNumber(warrantPrice).toLocaleString('en-US'),
    planType,
  };

  if (!mode)
    return <TableRow columnOrder={columns} fields={{ ...vestingRowData, ...planRowData }} />;

  const requestedColumns = new Set(columns);

  if (type === 'planInfo')
    return table[sharePlanType].planColumns
      .filter((column) => requestedColumns.has(column))
      .map((column) => (
        <SummaryItem
          id={planRowData.id}
          key={column}
          planType={planRowData.planType}
          title={table[sharePlanType].planColumnsTitle[column]}
          value={planRowData[column]}
        />
      ));

  return table[sharePlanType].vestingColumns.map(
    (column) =>
      requestedColumns.has(column) &&
      column !== VestingExerciseStructureColumn.EXERCISE_BUTTON && (
        <SummaryItem
          id={planRowData.id}
          key={column}
          planType={planRowData.planType}
          title={table[sharePlanType].vestingColumnsTitle[column]}
          value={vestingRowData[column]}
        />
      ),
  );
};
